import { useCallback, useRef } from 'react';
import { Device } from '@twilio/voice-sdk';
import { getDevice } from '@/services/twilio/voice/deviceManager';
import { getAccessToken } from '@/services/twilio/voice/tokenService';
import { UseTwilioCallLogsReturn } from '../../useTwilioCallLogs';
import { logger } from '@/utils/logger';
export const useCallOperations = (
  deviceRef: React.MutableRefObject<Device | null>,
  callInProgress: boolean,
  callLogActions: UseTwilioCallLogsReturn,
  updateCallToConnecting: (contactId?: number, contactName?: string) => void,
  updateCallSid: (sid: string) => void,
  resetCall: () => void,
  clearExistingCalls: () => void,
  initializeDevice: () => Promise<Device | null>,
  startStatusPolling: (callSid: string) => void,
  stopStatusPolling: () => void,
  setIsMuted: (muted: boolean) => void,
  setupCallEventListeners: (call: any) => void,
  handleTokenExpiry?: () => Promise<Device | null>,
  userId?: number
) => {
  const callLogIdRef = useRef<number | null>(null);
  const activeCallRef = useRef<any>(null);

  const handleCallSidAvailable = useCallback(async (callSid: string) => {
    logger.debug('📞 [CALL-OPS] Call SID available:', { callSid });
    logger.debug('🔍 [CALL-OPS] Updating state and starting polling');
    updateCallSid(callSid);
    startStatusPolling(callSid);
    
    // Small delay to ensure state propagation
    setTimeout(() => {
      logger.debug('🔍 [CALL-OPS] CallSid should now be propagated to all hooks');
    }, 100);
  }, [updateCallSid, startStatusPolling]);

  const makeCall = useCallback(async (phoneNumber: string, contactId?: number, contactName?: string, leadId?: number) => {
    if (callInProgress) {
      logger.warn('⚠️ Call already in progress');
      return false;
    }

    logger.debug('📞 Making call to:', { phoneNumber });
    
    try {
      clearExistingCalls();

      // Create call log entry IMMEDIATELY before device operations
      const callLogId = await callLogActions.startCallLogging(phoneNumber, null, 'connecting', contactId, leadId);
      callLogIdRef.current = callLogId;

      // Use the deviceManager to get the current device
      let device = getDevice();
      
      // If no device or device is not ready, initialize it
      if (!device || device.state !== 'registered') {
        logger.debug('🔧 Device not ready, initializing...');
        device = await initializeDevice();
        if (!device) {
          throw new Error('Failed to initialize device');
        }
      }

      // Final check before making the call
      if (device.state !== 'registered') {
        throw new Error(`Device is not ready for calls. Current state: ${device.state}. Please try again in a few moments.`);
      }

      updateCallToConnecting(contactId, contactName);

      logger.debug('🎟️ Refreshing Twilio token with call metadata before connect', {
        call_user_id: userId,
        lead_id: leadId,
        lead_contact_id: contactId
      });
      const metadataToken = await getAccessToken({ userId, leadId, contactId });
      device.updateToken(metadataToken);

      logger.debug('📞 Connecting call with device state:', { state: device.state });
      const call = await device.connect({
        params: { 
          To: phoneNumber,
          ...(userId && { user_id: userId.toString() }),
          ...(contactId && { lead_contact_id: contactId.toString() }),
          ...(leadId && { lead_id: leadId.toString() })
        }
      });

      if (!call || !call.parameters) {
        throw new Error('Invalid call object returned from Twilio');
      }

      activeCallRef.current = call;
      setupCallEventListeners(call);

      const callSid = call.parameters.CallSid;
      if (callSid) {
        await callLogActions.updateCallSid(callSid);
        await callLogActions.updateCallProgress('connecting');
        await handleCallSidAvailable(callSid);
      }

      logger.debug('✅ Call initiated successfully');
      return true;

    } catch (error: any) {
      logger.error('❌ Error making call', error);
      
      // Provide user-friendly error messages
      let userFriendlyMessage = error.message;
      
      if (error.message?.includes('timeout') || error.message?.includes('registration')) {
        userFriendlyMessage = 'Device registration failed. Please check your internet connection and try again.';
      } else if (error.code === 20104 || error.code === 31205) {
        userFriendlyMessage = 'Authentication failed. Please refresh the page and try again.';
      } else if (error.message?.includes('permission')) {
        userFriendlyMessage = 'Microphone permission is required. Please allow microphone access and try again.';
      }
      
      // Handle token expiration during call attempt
      if ((error.code === 20104 || error.code === 31205 || 
           error.message?.includes('expired') || 
           error.message?.includes('AccessTokenExpired')) && 
          handleTokenExpiry) {
        logger.debug('🔄 Token expired during call, refreshing and retrying...');
        try {
          await handleTokenExpiry();
          // Retry the call with the refreshed token
          return await makeCall(phoneNumber, contactId, contactName, leadId);
        } catch (refreshError) {
          logger.error('❌ Failed to refresh token and retry call', refreshError as any);
          userFriendlyMessage = 'Authentication failed. Please refresh the page and try again.';
        }
      }
      
      resetCall();
      
      if (callLogIdRef.current) {
        await callLogActions.finalizeCallLogInDb('failed', userFriendlyMessage);
      }
      
      logger.error('👤 User-friendly error', new Error(userFriendlyMessage));
      
      return false;
    }
  }, [
    callInProgress,
    clearExistingCalls,
    initializeDevice,
    updateCallToConnecting,
    callLogActions,
    setupCallEventListeners,
    handleCallSidAvailable,
    resetCall,
    handleTokenExpiry,
    userId
  ]);

  const endCall = useCallback(async () => {
    logger.debug('📞 Ending call');
    
    try {
      if (activeCallRef.current) {
        activeCallRef.current.disconnect();
        activeCallRef.current = null;
      }
      
      stopStatusPolling();
      
      if (callLogIdRef.current) {
        await callLogActions.finalizeCallLogInDb('completed');
      }
      
      resetCall();
      return true;
    } catch (error) {
      logger.error('❌ Error ending call', error as any);
      resetCall();
      return false;
    }
  }, [stopStatusPolling, callLogActions, resetCall]);

  const toggleMute = useCallback(async (muted: boolean) => {
    try {
      if (activeCallRef.current) {
        activeCallRef.current.mute(muted);
        setIsMuted(muted);
        logger.debug(`🔇 Call ${muted ? 'muted' : 'unmuted'}`);
        return true;
      }
      return false;
    } catch (error) {
      logger.error('❌ Error toggling mute', error as any);
      return false;
    }
  }, [setIsMuted]);

  const sendDTMF = useCallback(async (digit: string) => {
    try {
      if (activeCallRef.current) {
        activeCallRef.current.sendDigits(digit);
        logger.debug(`📱 Sent DTMF: ${digit}`);
        return true;
      }
      return false;
    } catch (error) {
      logger.error('❌ Error sending DTMF', error as any);
      return false;
    }
  }, []);

  return {
    makeCall,
    endCall,
    toggleMute,
    sendDTMF,
    callLogIdRef,
    activeCallRef,
    handleCallSidAvailable
  };
};
